/**
 * The renderer seam.
 *
 * Two backends sit behind this: WebGL2 (M0-05's target — 60 fps with a full
 * screen of animated glyphs on integrated graphics) and Canvas2D, which must
 * render *identically*, only slower. Identical output is what makes the
 * fallback trustworthy: if the two disagree, one of them is wrong and nobody
 * will notice which until a player on old hardware reports something strange.
 *
 * A frame is two things, and the split matters:
 *
 * - **The terrain grid**, aligned to cells, scrolled by a whole-pixel camera
 *   offset. Flat typed arrays rather than an array of objects, because this is
 *   uploaded to the GPU every frame and 3,000 cells of garbage per frame is
 *   how a glyph grid ends up dropping frames on a machine that renders
 *   Doom fine.
 * - **Sprites**, positioned in arbitrary pixels. Actors are not cell-aligned
 *   while they walk — that is what "remote players glide, never teleport"
 *   means — so they cannot live in the grid.
 */

/** Packed `0xRRGGBB`. Alpha is carried separately where it is needed at all. */
export type Rgb = number;

export interface TerrainFrame {
  readonly columns: number;
  readonly rows: number;

  /** Atlas index per cell, row-major. */
  readonly glyphs: Uint16Array;
  readonly fg: Uint32Array;
  readonly bg: Uint32Array;

  /**
   * Whole-pixel scroll offset. The camera moves smoothly in tile space and is
   * snapped here: a glyph grid drawn on a half-pixel shimmers.
   */
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface Sprite {
  readonly glyph: number;
  readonly fg: Rgb;
  /** Pixel position within the canvas, already offset by the camera. */
  readonly x: number;
  readonly y: number;
}

export interface Renderer {
  readonly kind: 'webgl2' | 'canvas2d';

  /** Canvas pixel dimensions changed. */
  resize(width: number, height: number): void;

  draw(terrain: TerrainFrame, sprites: readonly Sprite[]): void;

  dispose(): void;
}

/** The atlas, once decoded, plus the layout the renderers need to index it. */
export interface GlyphAtlas {
  readonly image: HTMLImageElement | ImageBitmap;
  readonly columns: number;
  readonly rows: number;
  readonly cellWidth: number;
  readonly cellHeight: number;
}

/** Where a glyph sits in the atlas, in pixels. */
export function atlasCell(atlas: GlyphAtlas, glyph: number): { x: number; y: number } {
  const column = glyph % atlas.columns;
  const row = Math.floor(glyph / atlas.columns);
  return { x: column * atlas.cellWidth, y: row * atlas.cellHeight };
}

/** Allocate the flat arrays for a grid of this size. */
export function createTerrainFrame(columns: number, rows: number): TerrainFrame {
  const cells = columns * rows;
  return {
    columns,
    rows,
    glyphs: new Uint16Array(cells),
    fg: new Uint32Array(cells),
    bg: new Uint32Array(cells),
    offsetX: 0,
    offsetY: 0,
  };
}
